import { BaseApiService } from './baseApiService';

/**
 * Service for admin operations
 */
class AdminService extends BaseApiService {
  // Dashboard
  async getAdminDashboard() {
    await this.ensureInitialized();
    const response = await this.api.get('/admin/dashboard');
    return response.data;
  }

  async getAdminSettings() {
    await this.ensureInitialized();
    const response = await this.api.get('/admin/settings');
    return response.data;
  }

  // Orders
  async getAdminOrders(params?: any) {
    await this.ensureInitialized();
    const searchParams = new URLSearchParams(params);
    const response = await this.api.get(
      `/admin/orders?${searchParams.toString()}`
    );
    return response.data;
  }

  async getAdminOrder(orderId: number) {
    await this.ensureInitialized();
    const response = await this.api.get(`/admin/orders/${orderId}`);
    return response.data;
  }

  async updateOrderStatus(
    orderId: number,
    data: { status: string; trackingNumber?: string; notes?: string }
  ) {
    await this.ensureInitialized();
    const response = await this.api.put(
      `/admin/orders/${orderId}/status`,
      data
    );
    return response.data;
  }

  async sendOrderStatusEmail(orderId: number) {
    await this.ensureInitialized();
    const response = await this.api.post(`/admin/orders/${orderId}/send-email`);
    return response.data;
  }

  async getRecentOrders(count: number = 10) {
    await this.ensureInitialized();
    const response = await this.api.get(`/admin/orders/recent?count=${count}`);
    return response.data;
  }

  // Email Configuration
  async validateEmailConfiguration() {
    await this.ensureInitialized();
    const response = await this.api.get('/admin/email/validate');
    return response.data;
  }

  async sendTestEmail(toEmail?: string) {
    await this.ensureInitialized();
    const response = await this.api.post('/admin/email/test', { toEmail });
    return response.data;
  }

  async getEmailSettings() {
    await this.ensureInitialized();
    const response = await this.api.get('/admin/email/settings');
    return response.data;
  }

  async saveEmailSettings(settings: {
    adminEmail: string;
    orderCreatedNotifications: boolean;
    orderStatusNotifications: boolean;
    lowStockNotifications: boolean;
    customerServiceNotifications: boolean;
  }) {
    await this.ensureInitialized();
    const response = await this.api.put('/admin/email/settings', settings);
    return response.data;
  }

  // Email Templates
  async getEmailTemplates() {
    await this.ensureInitialized();
    const response = await this.api.get('/admin/email/templates');
    return response.data;
  }

  async getEmailTemplate(templateType: string) {
    await this.ensureInitialized();
    const response = await this.api.get(
      `/admin/email/templates/${templateType}`
    );
    return response.data;
  }

  async upsertEmailTemplate(template: {
    templateType: string;
    subject: string;
    htmlBody: string;
  }) {
    await this.ensureInitialized();
    const response = await this.api.put('/admin/email/templates', template);
    return response.data;
  }

  async previewEmailTemplate(
    templateType: string,
    data?: Record<string, string>
  ) {
    await this.ensureInitialized();
    const response = await this.api.post('/admin/email/templates/preview', {
      templateType,
      data,
    });
    return response.data;
  }

  // Users
  async checkAdminStatus() {
    await this.ensureInitialized();
    const response = await this.api.get('/admin/user/check-admin');
    return response.data;
  }

  async getAdminUsers() {
    await this.ensureInitialized();
    const response = await this.api.get('/admin/users');
    return response.data;
  }

  async searchUsers(params: any) {
    await this.ensureInitialized();
    const response = await this.api.get('/admin/users/search', { params });
    return response.data;
  }

  async updateUserRole(userId: number, role: string) {
    await this.ensureInitialized();
    const response = await this.api.put(`/admin/users/${userId}/role`, {
      role,
    });
    return response.data;
  }

  async updateUserStatus(userId: number, isActive: boolean) {
    await this.ensureInitialized();
    const response = await this.api.put(`/admin/users/${userId}/status`, {
      isActive,
    });
    return response.data;
  }

  // Cache
  async clearAdminCache(cacheKey?: string) {
    await this.ensureInitialized();
    const response = await this.api.post(
      '/admin/cache/clear',
      cacheKey ? { cacheKey } : undefined
    );
    return response.data;
  }

  // Stats
  async getAdvancedStats() {
    await this.ensureInitialized();
    const response = await this.api.get('/admin/stats/advanced');
    return response.data;
  }

  // Warmup
  async runWarmup() {
    await this.ensureInitialized();
    const response = await this.api.post('/admin/warmup/run');
    return response.data;
  }

  async getWarmupStatus() {
    await this.ensureInitialized();
    const response = await this.api.get('/admin/warmup/status');
    return response.data;
  }

  // Maintenance
  async cleanupAbandonedOrders() {
    await this.ensureInitialized();
    const response = await this.api.post('/admin/maintenance/cleanup-orders');
    return response.data;
  }

  // Bootstrap
  async bootstrapAdmin(adminData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }) {
    await this.ensureInitialized();
    const response = await this.api.post('/admin/bootstrap', adminData);
    return response.data;
  }

  // Debug
  async getDebugClaims() {
    await this.ensureInitialized();
    const response = await this.api.get('/admin/debug/claims');
    return response.data;
  }
}

export const adminService = new AdminService();
export default adminService;
